import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./../../../../assets/css/purchase/form.css";

import Breadcrumbs from "../../../components/Breadcrumbs";
import Topbar from "../../../components/Topbar";

import {
  getAlert,
  insertData,
  updateData,
  deleteData,
  fetchServiceDetails,
  createServiceDetail,
  updateServiceDetail,
  deleteServiceDetail,
  uploadServiceDetailImages,
  deleteServiceDetailImage,
} from "../../../../utils/SamuiUtils";

const MAX_IMAGES = 3;
const THEME_COLOR = "#E13F57";

const createEmptyDetail = () => ({
  key: `detail-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  detailId: null,
  serviceMethod: "",
  serviceAreaType: "",
  serviceDesc: "",
  images: [],
  imagesToDelete: [],
});

const cloneDetails = (details) =>
  details.map((detail) => ({
    ...detail,
    images: (detail.images || []).map((image) => ({ ...image })),
    imagesToDelete: [],
  }));

const ServiceForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  masterList = [],
  setMode,
  returnToServiceStep,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialFormData, setInitialFormData] = useState({});
  const [details, setDetails] = useState([]);
  const [initialDetails, setInitialDetails] = useState([]);
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const detailFileInputRefs = useRef({});
  const companyId = useMemo(
    () => window.localStorage.getItem("company") || "",
    []
  );

  const hasCompIdField = useMemo(() => {
    if (selectedData && Object.prototype.hasOwnProperty.call(selectedData, "Comp_Id")) {
      return true;
    }
    if (Array.isArray(masterList) && masterList.length > 0) {
      return masterList.some((item) =>
        Object.prototype.hasOwnProperty.call(item || {}, "Comp_Id")
      );
    }
    return Boolean(companyId);
  }, [masterList, selectedData, companyId]);

  const goBackToList = async () => {
    // ถ้ามาจากหน้าขั้นตอนบริการ ให้กลับไปที่หน้าขั้นตอนบริการ
    if (location.state?.create) {
      navigate("/servicestep");
      return;
    }

    // ถ้าไม่ใช่ ให้กลับไปที่หน้า Service List ตามปกติ
    if (typeof callInitialize === "function") {
      try {
        await callInitialize();
        return;
      } catch (err) {
        console.error("callInitialize failed:", err);
      }
    }

    if (typeof setMode === "function") {
      setMode("S");
    } else {
      window.location.href = "/service";
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target?.tagName === "INPUT" ||
        event.target?.tagName === "TEXTAREA" ||
        event.target?.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            goBackToList();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, formData, details]);

  useEffect(() => {
    const loadForInsert = () => {
      setFormData((prev) => {
        const defaultData = {
          Service_Code: prev?.Service_Code || "",
          Service_Name: "",
          Service_Name_EN: "",
          Service_Status: "Y",
          Service_Desc: "",
          ...(hasCompIdField
            ? { Comp_Id: companyId || window.localStorage.getItem("company") }
            : {}),
        };
        setInitialFormData(defaultData);
        return defaultData;
      });
      setDetails([]);
      setInitialDetails([]);
      setDeletedDetailIds([]);
    };

    const loadForUpdate = async () => {
      if (!selectedData) {
        return;
      }

      const existingData = {
        Service_Id: selectedData.Service_Id,
        Service_Code: selectedData.Service_Code || "",
        Service_Name: selectedData.Service_Name || "",
        Service_Name_EN: selectedData.Service_Name_EN || "",
        Service_Status: selectedData.Service_Status || "Y",
        Service_Desc: selectedData.Service_Desc || "",
        ...(hasCompIdField
          ? { Comp_Id: selectedData.Comp_Id || companyId || undefined }
          : {}),
      };

      setFormData(existingData);
      setInitialFormData(existingData);
      setDeletedDetailIds([]);

      try {
        const response = await fetchServiceDetails(selectedData.Service_Id);
        if (response?.status !== "OK") {
          setDetails([]);
          setInitialDetails([]);
          return;
        }

        const mappedDetails = (response.details || []).map((detail) => ({
          key: `existing-${detail.serviceDetailId}`,
          detailId: detail.serviceDetailId,
          serviceMethod: detail.serviceMethod || "",
          serviceAreaType: detail.serviceAreaType || "",
          serviceDesc: detail.serviceDesc || "",
          images: (detail.images || []).map((image) => ({
            key: `img-${image.imgId}`,
            imgId: image.imgId,
            name: image.name,
            type: image.type,
            dataUrl: image.dataUrl || null,
          })),
          imagesToDelete: [],
        }));

        setDetails(mappedDetails);
        setInitialDetails(cloneDetails(mappedDetails));
      } catch (error) {
        console.error("Failed to load service details:", error);
        setDetails([]);
        setInitialDetails([]);
      }
    };

    if (mode === "I") {
      loadForInsert();
    } else if (mode === "U") {
      loadForUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedData, hasCompIdField]);

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleAddDetail = () => {
    setDetails((prev) => [...prev, createEmptyDetail()]);
  };

  const handleDetailChange = (detailKey, field, value) => {
    setDetails((prev) =>
      prev.map((detail) =>
        detail.key === detailKey ? { ...detail, [field]: value } : detail
      )
    );
  };

  const handleRemoveDetail = (detailKey) => {
    setDetails((prev) => {
      const target = prev.find((detail) => detail.key === detailKey);
      if (target?.detailId) {
        setDeletedDetailIds((ids) => [...ids, target.detailId]);
      }
      return prev.filter((detail) => detail.key !== detailKey);
    });
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  const handleDetailImageSelection = async (detailKey, files) => {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;

    const targetDetail = details.find((item) => item.key === detailKey);
    if (!targetDetail) return;

    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
      "image/gif",
    ];

    const invalidFiles = fileList.filter((file) => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map((file) => file.name).join(", ");
      getAlert(
        "WARNING",
        `ไฟล์ต่อไปนี้ไม่รองรับ: ${invalidNames}`,
        "กรุณาเลือกไฟล์ประเภท PNG, JPG, JPEG, WEBP หรือ GIF เท่านั้น"
      );
      return;
    }

    const currentCount = (targetDetail.images || []).length;
    if (currentCount + fileList.length > MAX_IMAGES) {
      getAlert("WARNING", `สามารถเพิ่มรูปภาพได้สูงสุด ${MAX_IMAGES} รูปต่อแถว`);
      return;
    }

    if (isSavingRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const newImages = await Promise.all(
        fileList.map(async (file) => ({
          key: `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          imgId: null,
          name: file.name,
          type: file.type,
          dataUrl: await readFileAsDataUrl(file),
        }))
      );

      setDetails((prev) =>
        prev.map((detail) =>
          detail.key === detailKey
            ? {
              ...detail,
              images: [...(detail.images || []), ...newImages].slice(0, MAX_IMAGES),
            }
            : detail
        )
      );

      if (detailFileInputRefs.current[detailKey]) {
        detailFileInputRefs.current[detailKey].value = "";
      }
    } catch (error) {
      console.error("Failed to read detail images:", error);
      getAlert("FAILED", "ไม่สามารถโหลดรูปภาพได้");
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleRemoveDetailImage = (detailKey, imageKey) => {
    setDetails((prev) =>
      prev.map((detail) => {
        if (detail.key !== detailKey) return detail;

        const targetImage = (detail.images || []).find((image) => image.key === imageKey);
        let imagesToDelete = detail.imagesToDelete || [];

        if (targetImage?.imgId) {
          imagesToDelete = Array.from(new Set([...imagesToDelete, targetImage.imgId]));
        }

        return {
          ...detail,
          images: (detail.images || []).filter((image) => image.key !== imageKey),
          imagesToDelete,
        };
      })
    );
  };

  const sanitizePayload = (data) => {
    const payload = { ...data };

    if (payload.Service_Code) {
      payload.Service_Code = payload.Service_Code.trim();
    }
    if (!payload.Service_Code) {
      delete payload.Service_Code;
    }

    payload.Service_Name = (payload.Service_Name || "").trim();
    payload.Service_Name_EN = (payload.Service_Name_EN || "").trim();
    payload.Service_Status = payload.Service_Status === "N" ? "N" : "Y";
    payload.Service_Desc = (payload.Service_Desc || "").trim();

    if (hasCompIdField) {
      const trimmedCompId =
        payload.Comp_Id != null ? String(payload.Comp_Id).trim() : null;
      const fallbackCompId = companyId ? String(companyId).trim() : null;
      payload.Comp_Id = trimmedCompId || fallbackCompId || null;
    } else {
      delete payload.Comp_Id;
    }

    if (payload.Service_Id !== undefined) {
      delete payload.Service_Id;
    }

    return payload;
  };

  const buildInsertArguments = (data) => {
    const prepared = { ...data };
    const entries = Object.entries(prepared).filter(([, value]) => value !== undefined);
    const fields = entries.map(([key]) => key).join(", ");
    const values = entries.map(([, value]) => value);
    return { fields, values };
  };

  const buildUpdateValues = (data) => {
    const prepared = { ...data };
    Object.keys(prepared).forEach((key) => {
      if (prepared[key] === undefined) {
        delete prepared[key];
      }
    });
    delete prepared.Service_Id;
    return prepared;
  };

  const handleInsertService = async (payload) => {
    const { fields, values } = buildInsertArguments(payload);
    const response = await insertData("Tb_Set_Service", fields, values);
    if (response?.status !== "OK") {
      throw new Error(response?.message || "ไม่สามารถบันทึกข้อมูลได้");
    }
    const insertedRecord =
      Array.isArray(response?.data) && response.data.length > 0
        ? response.data[0]
        : null;
    return insertedRecord;
  };

  const handleUpdateService = async (payload, currentServiceId) => {
    if (!currentServiceId) {
      throw new Error("ไม่พบรหัสบริการสำหรับการอัปเดต");
    }
    const where = { Service_Id: currentServiceId };
    const values = buildUpdateValues(payload);
    const response = await updateData("Tb_Set_Service", values, where);
    if (response?.status === "FAILED") {
      throw new Error(response?.message || "ไม่สามารถอัปเดตข้อมูลได้");
    }
    return response;
  };

  const handleDelete = async () => {
    if (!selectedData?.Service_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (!isConfirm) return;
      try {
        await deleteData(
          "Tb_Set_Service",
          `WHERE Service_Id = ${selectedData.Service_Id}`
        );
        await callInitialize();
      } catch (error) {
        getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
      }
    });
  };

  const handleReset = () => {
    if (!initialFormData) return;
    setFormData(initialFormData);
    setDetails(cloneDetails(initialDetails));
    setDeletedDetailIds([]);
  };

  const validateDetails = () => {
    for (const detail of details) {
      if ((detail.images || []).length > MAX_IMAGES) {
        getAlert("WARNING", `รูปภาพต่อแถวต้องไม่เกิน ${MAX_IMAGES} รูป`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    const payload = sanitizePayload({
      ...formData,
      Comp_Id: formData.Comp_Id || companyId || undefined,
    });

    if (!payload.Service_Name) {
      getAlert("WARNING", "กรุณากรอกชื่อบริการ");
      return;
    }

    if (payload.Service_Code?.length > 10) {
      getAlert("WARNING", "รหัสบริการต้องไม่เกิน 10 ตัวอักษร");
      return;
    }

    if (!validateDetails()) {
      return;
    }

    const serviceId = selectedData?.Service_Id || null;

    const duplicateByCode = masterList.some((item) => {
      if (!payload.Service_Code) return false;
      if (mode === "U" && item.Service_Id === serviceId) return false;
      return (
        (item.Service_Code || "").trim().toLowerCase() ===
        payload.Service_Code.trim().toLowerCase()
      );
    });

    if (duplicateByCode) {
      getAlert("WARNING", "รหัสบริการนี้มีอยู่ในระบบแล้ว");
      return;
    }

    const duplicateByName = masterList.some((item) => {
      if (mode === "U" && item.Service_Id === serviceId) return false;
      return (
        (item.Service_Name || "").trim().toLowerCase() ===
        payload.Service_Name.trim().toLowerCase()
      );
    });

    if (duplicateByName) {
      getAlert("WARNING", "ชื่อบริการนี้มีอยู่ในระบบแล้ว");
      return;
    }

    try {
      let savedServiceId = serviceId;

      if (mode === "I") {
        if (hasCompIdField && !payload.Comp_Id) {
          payload.Comp_Id = companyId || window.localStorage.getItem("company") || null;
        }

        const insertedService = await handleInsertService(payload);
        savedServiceId =
          insertedService?.Service_Id ||
          insertedService?.ServiceId ||
          insertedService?.ServiceID ||
          null;
        if (!savedServiceId) {
          throw new Error("ไม่สามารถอ่านรหัสบริการที่สร้างใหม่ได้");
        }
      } else if (mode === "U") {
        await handleUpdateService(payload, savedServiceId);
      }

      for (const detailId of deletedDetailIds) {
        try {
          await deleteServiceDetail(detailId);
        } catch (error) {
          console.error(`Failed to delete service detail ${detailId}:`, error);
        }
      }

      const trimToEmpty = (val) => {
        if (val === null || val === undefined) return "";
        return String(val).trim();
      };

      for (const detail of details) {
        const baseDetailPayload = {
          serviceMethod: trimToEmpty(detail.serviceMethod),
          serviceAreaType: trimToEmpty(detail.serviceAreaType),
          serviceDesc: trimToEmpty(detail.serviceDesc),
        };

        if (detail.detailId) {
          await updateServiceDetail(detail.detailId, baseDetailPayload);

          if (Array.isArray(detail.imagesToDelete)) {
            for (const imageId of detail.imagesToDelete) {
              try {
                await deleteServiceDetailImage(imageId);
              } catch (error) {
                console.error(`Failed to delete image ${imageId}:`, error);
              }
            }
          }

          const newImages = (detail.images || []).filter(
            (image) => !image.imgId && image.dataUrl
          );
          if (newImages.length > 0) {
            await uploadServiceDetailImages(
              detail.detailId,
              newImages.map((image) => ({
                name: image.name,
                mimeType: image.type,
                dataUrl: image.dataUrl,
              }))
            );
          }
        } else {
          const createPayload = {
            ...baseDetailPayload,
            images: (detail.images || []).map((image) => ({
              name: image.name,
              mimeType: image.type,
              dataUrl: image.dataUrl,
            })),
          };

          await createServiceDetail(savedServiceId, createPayload);
        }
      }

      // ถ้ามี returnToServiceStep callback และเป็นโหมดสร้าง ให้กลับไปที่หน้าขั้นตอนบริการ
      if (mode === "I" && typeof returnToServiceStep === "function") {
        returnToServiceStep(savedServiceId);
      } else {
        await callInitialize();
      }
    } catch (error) {
      console.error("Failed to save service:", error);
      getAlert("FAILED", error.message || "ไม่สามารถบันทึกข้อมูลได้");
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <>
      <Topbar
        breadcrumbs={[
          { label: name, link: "/service" },
          { label: mode === "U" ? `แก้ไข${name}` : `สร้าง${name}` },
        ]}
      />
      <div className="page-header mb-3">
        <Breadcrumbs
          isShowStatus={mode === "U"}
          items={[
            { name: name, url: "/service" },
            { name: mode === "U" ? `แก้ไข${name}` : `สร้าง${name}`, url: "#" },
          ]}
        />
      </div>

      <div className="container-fluid px-4 py-3">
        <div className="card shadow-sm mb-4 border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div
            className="card-header py-3"
            style={{
              backgroundColor: "#f9fafb",
              borderBottom: "2px solid #e5e7eb"
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="fas fa-info-circle" style={{ color: THEME_COLOR, fontSize: "18px" }}></i>
              <h5 className="mb-0" style={{ fontWeight: "600", color: "#111827" }}>ข้อมูลบริการ</h5>
            </div>
          </div>
          <div className="card-body p-4">
            <div className="row g-4">
              <div className="col-md-4">
                <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                  ชื่อบริการ <span style={{ color: THEME_COLOR }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="Service_Name"
                  value={formData?.Service_Name || ""}
                  onChange={handleChange}
                  placeholder="กรอกชื่อบริการ"
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "14px",
                    transition: "all 0.2s ease"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = THEME_COLOR;
                    e.target.style.boxShadow = `0 0 0 3px rgba(225, 63, 87, 0.1)`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "14px", whiteSpace: "nowrap" }}>
                  ชื่อบริการ (ภาษาอังกฤษ)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="Service_Name_EN"
                  value={formData?.Service_Name_EN || ""}
                  onChange={handleChange}
                  placeholder="Service Name (English)"
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "14px",
                    transition: "all 0.2s ease"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = THEME_COLOR;
                    e.target.style.boxShadow = `0 0 0 3px rgba(225, 63, 87, 0.1)`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                  สถานะ
                </label>
                <select
                  className="form-select"
                  name="Service_Status"
                  value={formData?.Service_Status || "Y"}
                  onChange={handleChange}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "14px"
                  }}
                >
                  <option value="Y">ใช้งาน</option>
                  <option value="N">ไม่ใช้งาน</option>
                </select>
              </div>

              <div className="col-12">
                <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "14px", whiteSpace: "nowrap" }}>
                  รายละเอียดบริการ
                </label>
                <textarea
                  className="form-control"
                  name="Service_Desc"
                  value={formData?.Service_Desc || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="กรอกรายละเอียดเพิ่มเติมของบริการ..."
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "12px 14px",
                    fontSize: "14px",
                    resize: "vertical",
                    minHeight: "140px",
                    transition: "all 0.2s ease"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = THEME_COLOR;
                    e.target.style.boxShadow = `0 0 0 3px rgba(225, 63, 87, 0.1)`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card shadow-sm mb-4 border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div
            className="card-header py-3"
            style={{
              backgroundColor: "#f9fafb",
              borderBottom: "2px solid #e5e7eb"
            }}
          >
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-list-ul" style={{ color: THEME_COLOR, fontSize: "18px" }}></i>
                <h5 className="mb-0" style={{ fontWeight: "600", color: "#111827" }}>รายละเอียดการให้บริการ</h5>
              </div>
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2"
                onClick={handleAddDetail}
                style={{
                  backgroundColor: THEME_COLOR,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: "500",
                  fontSize: "14px",
                  boxShadow: "0 2px 4px rgba(225, 63, 87, 0.2)",
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#c9354a";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = THEME_COLOR;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <i className="fas fa-plus"></i>
                <span>เพิ่มรายการ</span>
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {details.length === 0 ? (
              <div className="text-center py-5" style={{ backgroundColor: "#fafafa" }}>
                <i className="fas fa-inbox" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }}></i>
                <h6 style={{ color: "#6b7280", fontWeight: "500" }}>ยังไม่มีรายละเอียดบริการ</h6>
                <p style={{ color: "#9ca3af", fontSize: "14px", marginTop: "8px" }}>
                  คลิกปุ่ม "เพิ่มรายการ" เพื่อเพิ่มรายละเอียดการให้บริการ
                </p>
              </div>
            ) : (
              <div className="p-3">
                {details.map((detail, index) => (
                  <div
                    key={detail.key}
                    className="mb-3 p-3 border"
                    style={{
                      borderRadius: "10px",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            backgroundColor: `${THEME_COLOR}15`,
                            color: THEME_COLOR,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "600",
                            fontSize: "14px"
                          }}
                        >
                          {index + 1}
                        </div>
                        <span style={{ fontWeight: "500", color: "#374151" }}>รายการที่ {index + 1}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center gap-1"
                        onClick={() => handleRemoveDetail(detail.key)}
                        style={{
                          backgroundColor: "#fee2e2",
                          color: "#dc2626",
                          border: "none",
                          borderRadius: "8px",
                          padding: "6px 12px",
                          fontSize: "13px",
                          fontWeight: "500",
                          transition: "all 0.2s ease"
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = "#dc2626";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = "#fee2e2";
                          e.currentTarget.style.color = "#dc2626";
                        }}
                      >
                        <i className="fas fa-trash-alt"></i>
                        <span>ลบ</span>
                      </button>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "13px" }}>
                          วิธีการให้บริการ
                        </label>
                        <textarea
                          className="form-control"
                          rows={6}
                          value={detail.serviceMethod || ""}
                          onChange={(event) =>
                            handleDetailChange(detail.key, "serviceMethod", event.target.value)
                          }
                          placeholder="ระบุวิธีการให้บริการ..."
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            padding: "12px",
                            fontSize: "13px",
                            resize: "vertical",
                            minHeight: "140px"
                          }}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "13px", whiteSpace: "nowrap" }}>
                          ประเภทพื้นที่ให้บริการ
                        </label>
                        <textarea
                          className="form-control"
                          rows={6}
                          value={detail.serviceAreaType || ""}
                          onChange={(event) =>
                            handleDetailChange(detail.key, "serviceAreaType", event.target.value)
                          }
                          placeholder="ระบุประเภทพื้นที่..."
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            padding: "12px",
                            fontSize: "13px",
                            resize: "vertical",
                            minHeight: "140px"
                          }}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "13px" }}>
                          รายละเอียด
                        </label>
                        <textarea
                          className="form-control"
                          rows={6}
                          value={detail.serviceDesc || ""}
                          onChange={(event) =>
                            handleDetailChange(detail.key, "serviceDesc", event.target.value)
                          }
                          placeholder="รายละเอียดเพิ่มเติม..."
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            padding: "12px",
                            fontSize: "13px",
                            resize: "vertical",
                            minHeight: "140px"
                          }}
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label" style={{ fontWeight: "500", color: "#374151", fontSize: "13px" }}>
                          รูปภาพประกอบ <span style={{ color: "#9ca3af", fontSize: "12px" }}>(สูงสุด {MAX_IMAGES} รูป)</span>
                        </label>

                        {(detail.images || []).length > 0 && (
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {(detail.images || []).map((image) => (
                              <div
                                key={image.key}
                                className="position-relative"
                                style={{
                                  width: "140px",
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  border: "1px solid #e5e7eb",
                                  backgroundColor: "#fff",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                                }}
                              >
                                {image.dataUrl ? (
                                  <img
                                    src={image.dataUrl}
                                    alt={image.name}
                                    style={{
                                      width: "100%",
                                      height: "110px",
                                      objectFit: "cover",
                                      display: "block"
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "110px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#9ca3af",
                                      fontSize: "14px",
                                      backgroundColor: "#f9fafb"
                                    }}
                                  >
                                    <i className="fas fa-image fa-2x"></i>
                                  </div>
                                )}

                                <button
                                  type="button"
                                  onClick={() => handleRemoveDetailImage(detail.key, image.key)}
                                  style={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    backgroundColor: "rgba(220,38,38,0.95)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "28px",
                                    height: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
                                  }}
                                  title="ลบรูป"
                                >
                                  <i className="fas fa-times" style={{ fontSize: "12px" }}></i>
                                </button>

                                <div
                                  style={{
                                    padding: "8px",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    borderTop: "1px solid #e5e7eb",
                                    backgroundColor: "#ffffff"
                                  }}
                                  title={image.name}
                                >
                                  {image.name || "ไม่ทราบชื่อ"}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {(detail.images || []).length < MAX_IMAGES && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm d-flex align-items-center gap-2"
                              onClick={() =>
                                detailFileInputRefs.current[detail.key]?.click()
                              }
                              style={{
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                                border: "1px dashed #0369a1",
                                borderRadius: "8px",
                                padding: "10px 16px",
                                fontSize: "13px",
                                fontWeight: "500",
                                transition: "all 0.2s ease"
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#0369a1";
                                e.currentTarget.style.color = "#fff";
                                e.currentTarget.style.borderColor = "#0369a1";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "#e0f2fe";
                                e.currentTarget.style.color = "#0369a1";
                                e.currentTarget.style.borderColor = "#0369a1";
                              }}
                            >
                              <i className="fas fa-cloud-upload-alt"></i>
                              <span>อัปโหลดรูปภาพ</span>
                            </button>
                            <input
                              id={`detail-file-${detail.key}`}
                              ref={(element) => {
                                if (element) {
                                  detailFileInputRefs.current[detail.key] = element;
                                } else {
                                  delete detailFileInputRefs.current[detail.key];
                                }
                              }}
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                              multiple
                              style={{ display: "none" }}
                              onChange={(event) =>
                                handleDetailImageSelection(detail.key, event.target.files)
                              }
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex flex-wrap gap-2">
                <button
                  onClick={goBackToList}
                  type="button"
                  className="btn btn-lg d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#374151",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "0 24px",
                    height: "38px",
                    fontWeight: "500",
                    fontSize: "14px"
                  }}
                >
                  <i className="fas fa-arrow-left"></i>
                  <span>กลับหน้ารายการ</span>
                </button>
                <button
                  onClick={handleReset}
                  type="button"
                  className="btn btn-lg d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#0891b2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "0 24px",
                    height: "38px",
                    fontWeight: "500",
                    fontSize: "14px"
                  }}
                >
                  <i className="fas fa-redo"></i>
                  <span>เริ่มใหม่</span>
                </button>
                {mode === "U" && (
                  <button
                    onClick={() => navigate("/servicestep", {
                      state: {
                        selectServiceId: selectedData?.Service_Id,
                        fromService: true,
                        serviceName: formData?.Service_Name || selectedData?.Service_Name,
                      }
                    })}
                    type="button"
                    className="btn btn-lg d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#2f6df6",
                      color: "#fff",
                      border: "none",
                      borderRadius: "10px",
                      padding: "0 24px",
                    height: "38px",
                      fontWeight: "500",
                      fontSize: "14px"
                    }}
                  >
                    <i className="fas fa-list-ol"></i>
                    <span>ขั้นตอนบริการ</span>
                  </button>
                )}
                {mode === "U" && (
                  <button
                    onClick={handleDelete}
                    type="button"
                    className="btn btn-lg d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#dc2626",
                      color: "#fff",
                      border: "none",
                      borderRadius: "10px",
                      padding: "0 24px",
                    height: "38px",
                      fontWeight: "500",
                      fontSize: "14px"
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>
                    <span>ลบข้อมูล</span>
                  </button>
                )}
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  onClick={handleSubmit}
                  type="button"
                  className="btn btn-lg d-flex align-items-center justify-content-center gap-2"
                  style={{
                    position: "relative",
                    backgroundColor: mode === "U" ? "#f59e0b" : THEME_COLOR,
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "0 24px",
                    height: "38px",
                    fontWeight: "600",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    minWidth: "160px",
                    boxShadow: `0 4px 12px ${mode === "U" ? "rgba(245, 158, 11, 0.3)" : "rgba(225, 63, 87, 0.3)"}`,
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = `0 6px 16px ${mode === "U" ? "rgba(245, 158, 11, 0.4)" : "rgba(225, 63, 87, 0.4)"}`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 4px 12px ${mode === "U" ? "rgba(245, 158, 11, 0.3)" : "rgba(225, 63, 87, 0.3)"}`;
                  }}
                >
                  <i className="fas fa-save" />
                  <span>บันทึก</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceForm;
